import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';

const TYPES = [
  { value: 'note', label: 'Notiz' },
  { value: 'phone_in', label: 'Telefonat eingehend' },
  { value: 'phone_out', label: 'Telefonat ausgehend' },
  { value: 'messenger_in', label: 'Messenger eingehend' },
  { value: 'messenger_out', label: 'Messenger ausgehend' },
  { value: 'email_in', label: 'E-Mail eingehend' },
  { value: 'email_out', label: 'E-Mail ausgehend' },
  { value: 'personal', label: 'Persönlich' },
];

export default function JournalMagicEntry() {
  const { token } = useParams();
  const [valid, setValid] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ customer_id: '', contact_id: '', type: 'note', body: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        await api.get(`/journal-magic/${token}`);
        setValid(true);
        const res = await api.get(`/journal-magic/${token}/customers`);
        setCustomers(res.data.customers);
      } catch {
        setValid(false);
      }
    };
    validate();
  }, [token]);

  useEffect(() => {
    if (!form.customer_id) { setContacts([]); return; }
    const fetchContacts = async () => {
      try {
        const res = await api.get(`/customers/${form.customer_id}/contacts`);
        setContacts(res.data);
      } catch {
        setContacts([]);
      }
    };
    fetchContacts();
  }, [form.customer_id]);

  const handleSubmit = async () => {
    if (!form.customer_id) { toast.error('Bitte Kunde auswählen'); return; }
    if (!form.body.trim()) { toast.error('Bitte Text eingeben'); return; }
    setSaving(true);
    try {
      await api.post(`/journal-magic/${token}`, {
        customer_id: form.customer_id,
        contact_id: form.contact_id || null,
        type: form.type,
        body: form.body,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ customer_id: '', contact_id: '', type: 'note', body: '' });
    setContacts([]);
    setSuccess(false);
  };

  if (valid === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">Lädt...</p>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-700 font-medium">Ungültiger oder abgelaufener Link.</p>
          <p className="text-gray-400 text-sm mt-1">Bitte einen neuen Link anfordern.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Emge-Computer" className="h-14 w-14 object-contain" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle size={48} className="text-teal-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Eintrag gespeichert</h2>
              <p className="text-sm text-gray-500 mb-6">Der Journal-Eintrag wurde erfolgreich erfasst.</p>
              <button
                onClick={handleReset}
                className="bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
              >
                Weiteren Eintrag erfassen
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-gray-800">Journal-Eintrag erfassen</h2>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Kunde *</label>
                <select
                  value={form.customer_id}
                  onChange={e => setForm(f => ({ ...f, customer_id: e.target.value, contact_id: '' }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Bitte wählen...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {contacts.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Ansprechpartner (optional)</label>
                  <select
                    value={form.contact_id}
                    onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    <option value="">Kein Ansprechpartner</option>
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  {TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Text *</label>
                <textarea
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={5}
                  placeholder="Was wurde besprochen oder notiert?"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={saving}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
              >
                {saving ? 'Wird gespeichert...' : 'Eintrag speichern'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
